import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MaraSportsLogo from '../components/MaraSportsLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { cartApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Trophy, ShoppingCart, Trash2, ArrowLeft, Loader2,
    CreditCard, CheckCircle, Calendar, MapPin, Wallet, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
    { id: 'Stripe', name: 'Pay with Card (Stripe)', icon: '💳', isStripe: true },
    { id: 'M-Pesa', name: 'M-Pesa', icon: '📱' },
    { id: 'Cash', name: 'Cash', icon: '💵' },
    { id: 'Bank Transfer', name: 'Bank Transfer', icon: '🏦' }
];

const CartPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isAuthenticated, user, loading: authLoading, token } = useAuth();
    const [cart, setCart] = useState({ items: [], total: 0, currency: 'KES', item_count: 0 });
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('Stripe');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [paymentNotes, setPaymentNotes] = useState('');
    const [currency, setCurrency] = useState('KES');
    const [checkingPayment, setCheckingPayment] = useState(false);

    // Check for returning from Stripe
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId && token) {
            checkPaymentStatus(sessionId);
        }
    }, [searchParams, token]);

    useEffect(() => {
        if (authLoading) return;
        
        if (!isAuthenticated) {
            navigate('/auth');
            return;
        }
        loadCart();
    }, [isAuthenticated, authLoading, navigate]);

    const loadCart = async () => {
        try {
            const res = await cartApi.getCart();
            setCart(res.data);
        } catch (error) {
            console.error('Failed to load cart:', error);
            toast.error('Failed to load cart');
        } finally {
            setLoading(false);
        }
    };

    const checkPaymentStatus = async (sessionId) => {
        setCheckingPayment(true);
        let attempts = 0;
        const maxAttempts = 5;
        const pollInterval = 2000;

        const poll = async () => {
            if (attempts >= maxAttempts) {
                toast.error('Payment status check timed out. Please check your email for confirmation.');
                setCheckingPayment(false);
                return;
            }

            try {
                const response = await axios.get(
                    `${API_URL}/api/payments/status/${sessionId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.payment_status === 'paid') {
                    toast.success('Payment successful! Your registrations have been confirmed.');
                    setCheckingPayment(false);
                    navigate('/dashboard');
                    return;
                } else if (response.data.status === 'expired') {
                    toast.error('Payment session expired. Please try again.');
                    setCheckingPayment(false);
                    return;
                }

                // Continue polling
                attempts++;
                setTimeout(poll, pollInterval);
            } catch (error) {
                console.error('Error checking payment status:', error);
                attempts++;
                setTimeout(poll, pollInterval);
            }
        };

        poll();
    };

    const handleRemoveItem = async (registrationId) => {
        setRemovingId(registrationId);
        try {
            await cartApi.removeItem(registrationId);
            toast.success('Item removed from cart');
            loadCart();
        } catch (error) {
            toast.error('Failed to remove item');
        } finally {
            setRemovingId(null);
        }
    };

    const handleCheckout = async () => {
        setShowPaymentDialog(true);
    };

    const handleStripeCheckout = async () => {
        setProcessing(true);
        try {
            const response = await axios.post(
                `${API_URL}/api/payments/initiate`,
                {
                    origin_url: window.location.origin,
                    currency: currency
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Redirect to Stripe Checkout
            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                throw new Error('No checkout URL received');
            }
        } catch (error) {
            console.error('Stripe checkout error:', error);
            toast.error(error.response?.data?.detail || 'Failed to initiate payment');
            setProcessing(false);
        }
    };

    const handleConfirmPayment = async () => {
        // If Stripe selected, redirect to Stripe Checkout
        if (paymentMethod === 'Stripe') {
            await handleStripeCheckout();
            return;
        }

        // Otherwise, use traditional checkout
        setProcessing(true);
        try {
            const res = await cartApi.checkout({
                payment_method: paymentMethod,
                reference_number: referenceNumber || null,
                notes: paymentNotes || null
            });
            toast.success(`Payment successful! ${res.data.items_confirmed} registration(s) confirmed.`);
            setShowPaymentDialog(false);
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Checkout failed');
        } finally {
            setProcessing(false);
        }
    };

    const getCurrencySymbol = (curr) => {
        const symbols = { KES: 'KSh ', USD: '$', EUR: '€', GBP: '£' };
        return symbols[curr] || curr + ' ';
    };

    const getDisplayTotal = () => {
        if (currency === 'USD') {
            return `$${(cart.total / 150).toFixed(2)}`; // Approximate conversion
        }
        return `${getCurrencySymbol('KES')}${cart.total?.toLocaleString()}`;
    };

    // Show loading while checking payment
    if (checkingPayment) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <h2 className="font-heading text-xl uppercase mb-2">Verifying Payment</h2>
                    <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
                </div>
            </div>
        );
    }

    // Show loading while auth is initializing
    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />

            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Link to="/tournaments" className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-4 h-4" />
                                <span className="text-sm hidden sm:inline">Continue Shopping</span>
                            </Link>
                        </div>
                        <Link to="/" className="flex items-center gap-2">
                            <MaraSportsLogo className="h-8" />
                            <span className="font-heading font-bold text-lg tracking-tight uppercase hidden sm:block">
                                NextGen<span className="text-primary"> Tournaments</span>
                            </span>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-12">
                <div className="flex items-center gap-4 mb-8">
                    <ShoppingCart className="w-8 h-8 text-primary" />
                    <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase">
                        Your Cart
                    </h1>
                </div>

                {cart.items.length === 0 ? (
                    <div className="bg-[#0d2847] border border-white/5 p-12 text-center">
                        <ShoppingCart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <h2 className="font-heading text-xl uppercase mb-2">Your cart is empty</h2>
                        <p className="text-muted-foreground mb-6">Browse tournaments and add registrations to your cart</p>
                        <Link to="/tournaments">
                            <Button className="bg-primary hover:bg-red-600 text-white uppercase tracking-widest">
                                Browse Tournaments
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Cart Items */}
                        <div className="lg:col-span-2 space-y-4">
                            {cart.items.map((item) => (
                                <div
                                    key={item.id}
                                    className="bg-[#0d2847] border border-white/5 p-6"
                                    data-testid={`cart-item-${item.id}`}
                                >
                                    <div className="flex justify-between items-start gap-4">
                                        <div className="flex-1">
                                            <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-2">
                                                {item.tournament_name}
                                            </h3>
                                            <div className="space-y-1 text-sm text-muted-foreground">
                                                <p className="flex items-center gap-2">
                                                    <Trophy className="w-4 h-4" />
                                                    {item.sport}
                                                </p>
                                                <p>Category: {item.age_category}</p>
                                                {item.gender_category && (
                                                    <p>Gender: {item.gender_category}</p>
                                                )}
                                                {item.disciplines?.length > 0 && (
                                                    <div className="mt-2">
                                                        <p className="text-xs uppercase tracking-wider mb-1">Events:</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {item.disciplines.map((d, i) => (
                                                                <span key={i} className="px-2 py-0.5 bg-primary/10 border border-primary/20 text-xs">
                                                                    {d}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-mono text-xl text-accent">
                                                {getCurrencySymbol(item.currency || 'KES')}{(item.fee || item.registration_fee || 0).toLocaleString()}
                                            </p>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveItem(item.id)}
                                                disabled={removingId === item.id}
                                                className="text-muted-foreground hover:text-red-500 mt-2"
                                                data-testid={`remove-item-${item.id}`}
                                            >
                                                {removingId === item.id ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <Trash2 className="w-4 h-4" />
                                                )}
                                                <span className="ml-1 text-xs">Remove</span>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Order Summary */}
                        <div className="lg:col-span-1">
                            <div className="bg-[#0d2847] border border-white/5 p-6 sticky top-24">
                                <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6">
                                    Order Summary
                                </h3>

                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>Registrations</span>
                                        <span>{cart.item_count}</span>
                                    </div>
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>Subtotal</span>
                                        <span>{getCurrencySymbol(cart.currency)}{cart.total?.toLocaleString()}</span>
                                    </div>
                                    <div className="border-t border-white/10 pt-3 mt-3">
                                        <div className="flex justify-between items-center">
                                            <span className="font-bold">Total</span>
                                            <span className="font-mono text-2xl text-accent" data-testid="cart-total">
                                                {getCurrencySymbol(cart.currency)}{cart.total?.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    onClick={handleCheckout}
                                    disabled={processing || cart.items.length === 0}
                                    className="w-full bg-primary hover:bg-red-600 text-white uppercase tracking-widest mt-6 h-12"
                                    data-testid="checkout-btn"
                                >
                                    {processing ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <CreditCard className="w-4 h-4 mr-2" />
                                    )}
                                    {processing ? 'Processing...' : 'Proceed to Payment'}
                                </Button>

                                <p className="text-xs text-muted-foreground text-center mt-4">
                                    Secure payment via Stripe or local methods
                                </p>

                                <div className="mt-6 pt-6 border-t border-white/10">
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <CheckCircle className="w-4 h-4 text-success" />
                                        <span>Secure checkout</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                                        <CheckCircle className="w-4 h-4 text-success" />
                                        <span>Instant confirmation</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payment Method Dialog */}
                <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                    <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                                <Wallet className="w-5 h-5 text-primary" />
                                Payment Options
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="bg-[#0a1628] p-4 border border-white/10">
                                <p className="text-sm text-muted-foreground mb-1">Total Amount</p>
                                <p className="font-mono text-2xl text-accent">
                                    {getDisplayTotal()}
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Payment Method *</Label>
                                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10 h-12">
                                        <SelectValue placeholder="Select payment method" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        {PAYMENT_METHODS.map(method => (
                                            <SelectItem key={method.id} value={method.id}>
                                                <span className="flex items-center gap-2">
                                                    <span>{method.icon}</span>
                                                    <span>{method.name}</span>
                                                    {method.isStripe && (
                                                        <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">Recommended</span>
                                                    )}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Currency selection for Stripe */}
                            {paymentMethod === 'Stripe' && (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Currency</Label>
                                    <Select value={currency} onValueChange={setCurrency}>
                                        <SelectTrigger className="bg-[#0a1628] border-white/10 h-12">
                                            <SelectValue placeholder="Select currency" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            <SelectItem value="KES">
                                                <span className="flex items-center gap-2">
                                                    <span>🇰🇪</span>
                                                    <span>KES - Kenyan Shilling</span>
                                                </span>
                                            </SelectItem>
                                            <SelectItem value="USD">
                                                <span className="flex items-center gap-2">
                                                    <span>🇺🇸</span>
                                                    <span>USD - US Dollar</span>
                                                </span>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* Traditional payment fields */}
                            {paymentMethod !== 'Stripe' && (
                                <>
                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                                            Reference / Transaction Number
                                        </Label>
                                        <Input
                                            value={referenceNumber}
                                            onChange={(e) => setReferenceNumber(e.target.value)}
                                            placeholder="e.g., M-Pesa code, receipt number"
                                            className="bg-[#0a1628] border-white/10"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                                            Notes (Optional)
                                        </Label>
                                        <Input
                                            value={paymentNotes}
                                            onChange={(e) => setPaymentNotes(e.target.value)}
                                            placeholder="Any additional notes"
                                            className="bg-[#0a1628] border-white/10"
                                        />
                                    </div>
                                </>
                            )}

                            {paymentMethod === 'Stripe' ? (
                                <div className="bg-blue-900/20 border border-blue-500/30 p-3 text-sm">
                                    <p className="text-blue-400 flex items-center gap-2">
                                        <Globe className="w-4 h-4" />
                                        You'll be redirected to Stripe's secure payment page
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-green-900/20 border border-green-500/30 p-3 text-sm">
                                    <p className="text-green-400 flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4" />
                                        Payment will be automatically recorded in accounting
                                    </p>
                                </div>
                            )}
                        </div>
                        <DialogFooter className="gap-2">
                            <Button 
                                variant="outline" 
                                onClick={() => setShowPaymentDialog(false)} 
                                className="border-white/10"
                                disabled={processing}
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleConfirmPayment} 
                                className="bg-primary hover:bg-red-600"
                                disabled={processing}
                                data-testid="confirm-payment-btn"
                            >
                                {processing ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        Processing...
                                    </>
                                ) : paymentMethod === 'Stripe' ? (
                                    <>
                                        <CreditCard className="w-4 h-4 mr-2" />
                                        Pay with Stripe
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="w-4 h-4 mr-2" />
                                        Confirm Payment
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </main>
        </div>
    );
};

export default CartPage;
